import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Users, ClipboardList, Calendar, MoreHorizontal, BookText, Receipt, Mail, Wallet, MessageSquare, Compass, Sparkles, Inbox, Megaphone, LifeBuoy, NotebookPen, CalendarClock } from 'lucide-react';
import api from '../../services/api';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { MoreMenuDrawer } from './MoreMenuDrawer';
import { nachAlphabet } from '../../utils/alphabetisch';
import { WV_GEAENDERT } from '../../services/wiedervorlagenService';

/**
 * Mobile Bottom-Nav-Bar (PDF Emergent_UX_Polish – Aufgabe B1)
 * - Fixiert unten, weiß, Shadow-Top, Höhe 60 px
 * - Tabs rollenspezifisch, Leiste horizontal scrollbar (ca. 5 Tabs sichtbar, weitere per Wischen)
 * - „Home" zuerst, übrige Tabs alphabetisch (Projektregel); Chat mit Ungelesen-Zähler
 * - Aktiver Tab wird automatisch in den sichtbaren Bereich gescrollt
 * - Active: #2E86C1, 3 px Top-Border
 * - Touch-Target ≥44 px
 * - Versteckt in /chat/:roomId für Vollbild-Chat
 */
const ACTIVE_COLOR = '#2E86C1';

export default function MobileTabBar() {
  const { unreadTotal } = useChat();
  const { user } = useAuth();
  const location = useLocation();
  const role = user?.role;
  const isStudent = role === 'student' || !!user?.is_student;
  const isFamily = role === 'family_admin' || role === 'family_member';
  const [moreOpen, setMoreOpen] = useState(false);
  const scrollRef = useRef(null);
  const [fade, setFade] = useState({ left: false, right: false });

  const isInChatRoom = /^\/chat\/[^/]+/.test(location.pathname);

  // MarketingManager ist ein Projekttyp: GET /marketing/projekt liefert (und legt bei Freischaltung an)
  // das Standardprojekt. Ohne Freischaltung kommt kein Projekt zurück → kein Tab.
  const [marketingProjektId, setMarketingProjektId] = useState(null);
  useEffect(() => {
    if (!user || isStudent || isFamily) { setMarketingProjektId(null); return undefined; }
    let abgebrochen = false;
    api.get('/marketing/projekt')
      .then((res) => { if (!abgebrochen) setMarketingProjektId(res.data?.projekt?.id || null); })
      .catch(() => { if (!abgebrochen) setMarketingProjektId(null); });
    return () => { abgebrochen = true; };
  }, [user?.id, user?.tenant_id, isStudent, isFamily]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wiedervorlagen: Anzahl fälliger (heute + überfällig, eigene) als Zähler am Tab
  const [wvFaellig, setWvFaellig] = useState(0);
  useEffect(() => {
    if (!user || isStudent || isFamily) { setWvFaellig(0); return undefined; }
    let aktiv = true;
    const laden = () => {
      const d = new Date();
      const heute = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      api.get('/wiedervorlagen', { params: { status: 'offen', user_id: user.id, bis: heute } })
        .then((r) => { if (aktiv) setWvFaellig(r.data?.anzahl || 0); })
        .catch(() => { if (aktiv) setWvFaellig(0); });
    };
    laden();
    window.addEventListener(WV_GEAENDERT, laden);
    return () => { aktiv = false; window.removeEventListener(WV_GEAENDERT, laden); };
  }, [user?.id, isStudent, isFamily, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hinweis-Verlauf links/rechts, solange weitere Tabs per Wischen erreichbar sind
  const updateFade = () => {
    const el = scrollRef.current;
    if (!el) return;
    setFade({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  };
  // Aktiven Tab sichtbar halten
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const active = el.querySelector('[aria-current="page"]');
    if (active) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    updateFade();
  }, [location.pathname, isInChatRoom, marketingProjektId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.addEventListener('resize', updateFade);
    return () => window.removeEventListener('resize', updateFade);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isInChatRoom) return null;

  const chatTab = { name: 'Chat', href: '/chat', icon: MessageSquare, badge: unreadTotal };
  // Notizbuch für alle Rollen (Handy: Notizliste mit Vollbild-Editor)
  const notizenTab = { name: 'Notizen', href: '/notes', icon: NotebookPen };

  // Tabs je Rolle. Reihenfolge: „Home" zuerst, danach alphabetisch (Projektregel)
  let coreTabs;
  if (isStudent) {
    coreTabs = [
      { name: 'Home',      href: '/dashboard',  icon: Home },
      chatTab,
      // Bewerbungs-Kompass prominent als eigener Tab (alphabetisch direkt nach „Aufgaben“ sichtbar)
      { name: 'Bewerbung', href: '/student/bewerbung', icon: Compass, highlight: true },
      // Lerncoach (Frag die KI, Thema Studium) und Uni-Post mit Fristenradar – Handy zuerst
      { name: 'Frag die KI', href: '/student/ki',    icon: Sparkles, highlight: true },
      { name: 'Lernplan',  href: '/student/studyplan', icon: BookText },
      { name: 'Uni-Post',  href: '/student/post',      icon: Inbox },
      { name: "ToDo's",   href: '/tasks',      icon: ClipboardList },
      { name: 'Kalender',  href: '/calendar',   icon: Calendar },
      { name: 'E-Mail',    href: '/emails-mobile', icon: Mail },
      notizenTab,
    ];
  } else if (isFamily) {
    coreTabs = [
      { name: 'Home',     href: '/dashboard',       icon: Home },
      chatTab,
      { name: 'Adressen', href: '/addresses',       icon: Users },
      { name: 'Aufgaben', href: '/tasks',           icon: ClipboardList },
      { name: 'Einkauf',  href: '/family/shopping', icon: Receipt },
      { name: 'E-Mail',   href: '/emails-mobile',   icon: Mail },
      notizenTab,
    ];
  } else {
    // Buchhalter-Rollen (admin/manager) sehen "Buchhaltung" statt "Adressen"
    const isBuchhalter = role === 'admin' || role === 'manager' || role === 'super_admin';
    coreTabs = [
      { name: 'Home',     href: '/dashboard', icon: Home },
      chatTab,
      isBuchhalter
        ? { name: 'Buchhaltung', href: '/m/buchhaltung', icon: Wallet }
        : { name: 'Adressen', href: '/addresses', icon: Users },
      { name: 'Aufgaben', href: '/tasks',     icon: ClipboardList },
      { name: 'Kalender', href: '/calendar',  icon: Calendar },
      { name: 'E-Mail',   href: '/emails-mobile', icon: Mail },
      // „Frag die KI“ (KI-Berater) – auf dem Desktop ein Dashboard-Register, auf dem Handy eigene Seite
      { name: 'Frag die KI', href: '/ki', icon: Sparkles, highlight: true },
      notizenTab,
      { name: 'Wiedervorlagen', href: '/wiedervorlagen', icon: CalendarClock, badge: wvFaellig },
    ];
    // KrisenManager: wie in der Sidebar für Business-, Gastro-, Legal- und Mitglieder+-Mandanten
    const tType = user?.tenant_type || 'business';
    // Mitglieder+: Mitgliederliste direkt erreichbar (vorher nur über die Abteilungs-Kachel)
    if (tType === 'mitglieder_plus') {
      coreTabs.push({ name: 'Mitglieder', href: '/members?tab=members', icon: Users, aktivWenn: (pfad) => pfad.startsWith('/members') || pfad.startsWith('/m/mitglieder') });
    }
    if (['business', 'gastro', 'legal', 'mitglieder_plus'].includes(tType)) {
      coreTabs.push({ name: 'KrisenManager', href: '/krisenmanager', icon: LifeBuoy });
    }
    if (marketingProjektId) {
      coreTabs.push({
        name: 'MarketingManager', href: `/projects/${marketingProjektId}`, icon: Megaphone,
        aktivWenn: (pfad) => pfad.startsWith('/marketing') || pfad === `/projects/${marketingProjektId}`,
      });
    }
  }

  coreTabs = [coreTabs[0], ...nachAlphabet(coreTabs.slice(1), (tab) => tab.name)];

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-white md:hidden"
        style={{
          boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.06)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
        data-testid="mobile-tab-bar"
      >
        <div className="relative">
        <div
          ref={scrollRef}
          onScroll={updateFade}
          className="flex items-stretch overflow-x-auto no-scrollbar overscroll-x-contain snap-x"
          data-testid="mobile-tab-scroller"
        >
          {coreTabs.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.href}
                to={t.href}
                end={t.href === '/dashboard'}
                aria-current={t.aktivWenn ? (t.aktivWenn(location.pathname) ? 'page' : undefined) : undefined}
                className={({ isActive: navAktiv }) => { const isActive = t.aktivWenn ? t.aktivWenn(location.pathname) : navAktiv; return (
                  `relative shrink-0 min-w-[18%] px-2 snap-start flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${
                    isActive ? '' : 'text-gray-500 hover:text-gray-800'
                  }`); }
                }
                style={({ isActive: navAktiv }) => { const isActive = t.aktivWenn ? t.aktivWenn(location.pathname) : navAktiv; return ({
                  minHeight: 60,
                  borderTop: isActive ? `3px solid ${ACTIVE_COLOR}` : '3px solid transparent',
                  color: isActive ? ACTIVE_COLOR : undefined,
                }); }}
                data-testid={`mobile-tab-${t.aktivWenn ? 'marketing' : t.href.replace(/\//g, '-')}`}
              >
                <Icon style={{ width: 24, height: 24, color: t.highlight && !location.pathname.startsWith(t.href) ? '#059669' : undefined }} />
                <span className="whitespace-nowrap">{t.name}</span>
                {t.badge > 0 && (
                  <span
                    className="absolute top-1.5 left-1/2 ml-1.5 bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
                    data-testid={`mobile-tab-badge-${t.name.toLowerCase()}`}
                  >
                    {t.badge > 99 ? '99+' : t.badge}
                  </span>
                )}
              </NavLink>
            );
          })}

          {/* Mehr-Tab → Drawer (entfernt auf Wunsch 2026-05-24, führt zu Desktop-Umgebung)
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="relative flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-gray-500 hover:text-gray-800 transition-colors"
            style={{
              minHeight: 60,
              borderTop: moreOpen ? `3px solid ${ACTIVE_COLOR}` : '3px solid transparent',
              color: moreOpen ? ACTIVE_COLOR : undefined,
            }}
            data-testid="mobile-tab-more"
            aria-label="Weitere Menüpunkte öffnen"
          >
            <MoreHorizontal style={{ width: 24, height: 24 }} />
            <span>Mehr</span>
            {unreadTotal > 0 && (
              <span
                className="absolute top-1 right-3 bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
                data-testid="mobile-tab-badge-more"
              >
                {unreadTotal > 99 ? '99+' : unreadTotal}
              </span>
            )}
          </button>
          */}
        </div>
        {fade.left && <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white to-transparent" />}
        {fade.right && <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white to-transparent" data-testid="mobile-tab-fade-right" />}
        </div>
      </nav>

      <MoreMenuDrawer open={moreOpen} onOpenChange={setMoreOpen} />
    </>
  );
}
